/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { useEffect, useRef } from "react";
import { useNavigationWithProgress } from "@/hooks/utilities/use-navigation-with-progress";

/**
 * Adds a beforeunload prompt when the form is dirty and provides a helper to confirm navigation programmatically.
 * Note: App Router cannot block internal route changes globally; use confirmNavigate when navigating programmatically.
 */
export function useUnsavedChangesGuard(
  isDirty: boolean,
  message = "You have unsaved changes. Are you sure you want to leave?",
) {
  const router = useNavigationWithProgress();
  const dirtyRef = useRef(isDirty);
  dirtyRef.current = isDirty;

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!dirtyRef.current) return;
      e.preventDefault();
      e.returnValue = message;
      return message;
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [message]);

  const confirmNavigate = async (href: string) => {
    if (!dirtyRef.current) {
      router.push(href);
      return true;
    }
    const ok = window.confirm(message);
    if (ok) router.push(href);
    return ok;
  };

  return { confirmNavigate };
}
